import { useMemo } from "react";

import numeral from "numeral";

import { useTranslate } from "@similarweb/i18n-provider";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { ChangeCell } from "@similarweb/pro-logic-components";

import { LeaderValueCell } from "../../../common/widget/table/LeaderValueCell";
import { rowIndexColumn } from "../../../common/widget/table/rowIndexColumn";
import { useDomainColumn } from "../../../common/widget/table/useDomainColumn";
import { newsMetricsDataTypeTranslationKeyMap } from "../../utils/translationKeysConfig";

import type { ExtendedNewsMetricsData, ValueChangeData } from "./types";

const METRIC_COLUMN_WIDTH = 110;

export const useNewsMetricsColumns = (): ColumnsType<ExtendedNewsMetricsData> => {
    const { translate } = useTranslate();
    const domainColumn = useDomainColumn({ dataAutomation: "news-metrics-domain-cell" });

    return useMemo(() => {
        const createVolumeColumn = (category: string) => ({
            align: "right" as const,
            title: translate("common.volume"),
            width: METRIC_COLUMN_WIDTH,
            render: (data: ValueChangeData) => (
                <LeaderValueCell
                    dataAutomation={`news-metrics-${category}-volume-cell`}
                    isLeader={data.isLeader}
                    value={data.volume}
                />
            ),
        });

        const createChangeColumn = (category: string) => ({
            align: "right" as const,
            title: translate("global.change"),
            width: METRIC_COLUMN_WIDTH,
            render: (data: ValueChangeData) => (
                <ChangeCell
                    dataAutomation={`news-metrics-${category}-change-cell`}
                    formattedValue={data.change != null ? numeral(Math.abs(data.change)).format("0.00%") : undefined}
                    value={data.change ?? undefined}
                />
            ),
        });

        const storiesColumn = {
            children: [
                {
                    ...createVolumeColumn("stories"),
                    dataIndex: "stories" as const,
                    key: "stories-volume",
                },
                {
                    ...createChangeColumn("stories"),
                    dataIndex: "stories" as const,
                    key: "stories-change",
                },
            ],
            key: "stories",
            title: translate(newsMetricsDataTypeTranslationKeyMap.stories),
        };

        const pressReleasesColumn = {
            key: "pressReleases",
            children: [
                {
                    ...createVolumeColumn("press-releases"),
                    dataIndex: "pressReleases" as const,
                    key: "pressReleases-volume",
                },
                {
                    ...createChangeColumn("press-releases"),
                    dataIndex: "pressReleases" as const,
                    key: "pressReleases-change",
                },
            ],
            title: translate(newsMetricsDataTypeTranslationKeyMap.pressReleases),
        };

        const mentionsColumn = {
            key: "mentions",
            children: [
                {
                    ...createVolumeColumn("mentions"),
                    dataIndex: "mentions" as const,
                    key: "mentions-volume",
                },
                {
                    ...createChangeColumn("mentions"),
                    dataIndex: "mentions" as const,
                    key: "mentions-change",
                },
            ],
            title: translate(newsMetricsDataTypeTranslationKeyMap.mentions),
        };

        return [rowIndexColumn, domainColumn, storiesColumn, pressReleasesColumn, mentionsColumn];
    }, [domainColumn, translate]);
};
